import {useMemo} from 'react';
import {
  BankOutlined,
  BellOutlined,
  DollarOutlined,
  FileTextOutlined,
  FundOutlined,
  GiftOutlined,
  IdcardOutlined,
  LineChartOutlined,
  ProfileOutlined,
  ReloadOutlined,
  RiseOutlined,
  RocketOutlined,
  ShoppingCartOutlined,
  StarOutlined,
  TagsOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import {parseEventPayload} from '../../helpdeskTicketsUtils';
import {
  formatDateAbsolute,
  formatDateSmart,
  formatDateTimeAbsolute,
  formatMoney,
  humanize,
  shortenId,
  statusTone,
} from './format';
import styles from './SelectionCards.module.scss';

/* ------------------------------------------------------------------ */
/*  Selection collection                                              */
/* ------------------------------------------------------------------ */

// Walk events ASC, keep selections (USER_MESSAGE with payload.contextData),
// dedupe by contextData.type — latest pick per bucket wins, per spec §1.
const collectSelections = events => {
  if (!Array.isArray(events) || events.length === 0) return [];
  const sorted = [...events].sort(
    (a, b) =>
      new Date(a.createdAt || 0).getTime() -
      new Date(b.createdAt || 0).getTime(),
  );
  const byType = new Map();
  for (const event of sorted) {
    if (event?.eventType !== 'USER_MESSAGE') continue;
    const payload = parseEventPayload(event.payload);
    const ctx = payload?.contextData;
    if (!ctx || typeof ctx !== 'object') continue;
    const bucket = ctx.type || ctx.__typename || 'UNKNOWN';
    byType.set(bucket, {
      bucket,
      data: ctx,
      label: payload.text || '',
      createdAt: event.createdAt || null,
      eventId: event.id || null,
    });
  }
  return Array.from(byType.values());
};

/* ------------------------------------------------------------------ */
/*  Shared card primitives                                            */
/* ------------------------------------------------------------------ */

const Pill = ({status}) => {
  if (!status) return null;
  const tone = statusTone(status);
  return (
    <span className={`${styles.pill} ${styles[tone]}`}>{humanize(status)}</span>
  );
};

const Field = ({label, value, full, mono, tone, hideEmpty = true}) => {
  if (
    hideEmpty &&
    (value === null || value === undefined || value === '' || value === '—')
  ) {
    return null;
  }
  const valueClasses = [
    styles.value,
    mono ? styles.mono : '',
    tone === 'positive' ? styles.positive : '',
    tone === 'negative' ? styles.negative : '',
    tone === 'muted' ? styles.muted : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={`${styles.field} ${full ? styles.fieldFull : ''}`}>
      <span className={styles.label}>{label}</span>
      <span className={valueClasses}>{value}</span>
    </div>
  );
};

const IconBubble = ({imgUrl, fallback}) => (
  <span className={styles.iconWrap}>
    {imgUrl ? <img src={imgUrl} alt="" /> : fallback}
  </span>
);

const OpenButton = ({href, label = 'Open ↗'}) => {
  if (!href) {
    return (
      <button
        type="button"
        className={`${styles.openBtn} ${styles.openBtnDisabled}`}
        disabled>
        {label}
      </button>
    );
  }
  return (
    <a
      className={styles.openBtn}
      href={href}
      target="_blank"
      rel="noopener noreferrer">
      {label}
    </a>
  );
};

/* ------------------------------------------------------------------ */
/*  Per-type variants                                                 */
/* ------------------------------------------------------------------ */

// Routes live behind /dashboard/... in this admin. Where the spec lists a
// route that doesn't exist here yet (risk profile, watchlists, single
// dividend payout), the per-card function returns null and OpenButton goes
// disabled — the agent at least sees the data; the deep-link can land later.

const TransactionCard = ({selection}) => {
  const d = selection.data;
  const amount =
    d.totalTransactionAmountIncludingFees ?? d.transactionAmountNetOfFees;
  const net = d.transactionAmountNetOfFees;
  const fee = d.transactionFee;
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble fallback={<WalletOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>
            {formatMoney(amount, d.currency)}
          </span>
          <span className={styles.subtitle}>
            {[d.paymentGateway?.provider, formatDateSmart(d.createdAt)]
              .filter(Boolean)
              .map(humanize)
              .join(' · ')}
          </span>
        </div>
        <Pill status={d.status} />
      </div>
      <div className={styles.fields}>
        <Field label="Type" value={humanize(d.type)} />
        <Field label="Direction" value={humanize(d.direction)} />
        <Field label="Net of fees" value={formatMoney(net, d.currency)} />
        <Field label="Fee" value={formatMoney(fee, d.currency)} />
        <Field
          label="Balance before"
          value={formatMoney(d.balanceBefore, d.currency)}
        />
        <Field
          label="Balance after"
          value={formatMoney(d.balanceAfter, d.currency)}
        />
        <Field label="Reference" value={shortenId(d.referenceId)} mono full />
      </div>
      <OpenButton
        href={d.userId ? `/dashboard/user/${d.userId}/wallet-settings` : null}
        label="Open in wallet ↗"
      />
    </div>
  );
};

const UserContextCard = ({selection}) => {
  const d = selection.data;
  const profile = d.profile || {};
  const kyc = d.kyc || {};
  const sub = d.subscription || {};
  const fullName =
    [profile.firstName, profile.lastName].filter(Boolean).join(' ') ||
    profile.username ||
    'User';
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble fallback={<UserOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>{fullName}</span>
          <span className={styles.subtitle}>
            {[profile.countryISO, humanize(profile.userType)]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        <Pill status={kyc.kycStatus} />
      </div>
      <div className={styles.fields}>
        <Field label="Email" value={profile.email} full mono />
        <Field
          label="Display currency"
          value={profile.preferredDisplayCurrency}
        />
        <Field label="AML" value={humanize(kyc.amlStatus)} />
        <Field
          label="Document check"
          value={humanize(kyc.documentCheckStatus)}
        />
        <Field
          label="Daba Pro"
          value={
            sub.dabaPro?.subscriptionLabel ||
            (sub.dabaPro?.endDate ? 'Active' : 'Basic')
          }
        />
        <Field
          label="Simulator"
          value={
            sub.simulator?.planType ||
            (sub.simulator?.active ? 'Active' : 'Inactive')
          }
        />
        {Array.isArray(profile.userSegments) && profile.userSegments.length ? (
          <Field
            label="Segments"
            value={profile.userSegments.map(humanize).join(', ')}
            full
          />
        ) : null}
      </div>
      <OpenButton
        href={d.id ? `/dashboard/user/${d.id}/overview` : null}
        label="Open user profile ↗"
      />
    </div>
  );
};

const KycCard = ({selection}) => {
  const d = selection.data;
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble fallback={<IdcardOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>
            KYC: {humanize(d.kycStatus) || '—'}
          </span>
          <span className={styles.subtitle}>
            {[d.provider, formatDateAbsolute(d.kycApprovedAt)]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        <Pill status={d.kycStatus} />
      </div>
      <div className={styles.fields}>
        <Field label="AML" value={humanize(d.amlStatus)} />
        <Field label="Document check" value={humanize(d.documentCheckStatus)} />
        <Field label="Initiated" value={formatDateAbsolute(d.kycInitiatedAt)} />
        <Field
          label="AML approved"
          value={formatDateAbsolute(d.amlApprovedAt)}
        />
        <Field
          label="Doc approved"
          value={formatDateAbsolute(d.documentCheckApprovedAt)}
          full
        />
      </div>
      <OpenButton
        href={d.userId ? `/dashboard/user/${d.userId}/kyc-status` : null}
        label="Open KYC ↗"
      />
    </div>
  );
};

const PortfolioCard = ({selection}) => {
  const d = selection.data;
  const company = d.company || {};
  const instrument = d.instrument || {};
  const title = company.name || instrument.name || 'Position';
  const subtitle = [
    company.ticker || instrument.ticker,
    d.units
      ? `${formatMoney(d.units, null, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 6,
        })} units`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble imgUrl={company.logoImgURL} fallback={<RiseOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>{title}</span>
          <span className={styles.subtitle}>{subtitle}</span>
        </div>
        {instrument.listingType ? (
          <Pill status={instrument.listingType} />
        ) : null}
      </div>
      <div className={styles.fields}>
        <Field label="Amount" value={formatMoney(d.amount, d.assetCurrency)} />
        <Field
          label="Valuation"
          value={formatMoney(d.valuation, d.assetCurrency)}
        />
        <Field
          label="Last invested"
          value={formatDateSmart(d.lastInvestmentDate)}
          full
        />
      </div>
      <OpenButton
        href={d.userId ? `/dashboard/user/${d.userId}/portfolio` : null}
        label="Open portfolio ↗"
      />
    </div>
  );
};

const SubscriptionCard = ({selection}) => {
  const d = selection.data;
  const title =
    d.planLabel ||
    [humanize(d.category), humanize(d.planType)].filter(Boolean).join(': ') ||
    'Subscription';
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble fallback={<StarOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>{title}</span>
          <span className={styles.subtitle}>
            {[humanize(d.billingCycle), formatMoney(d.amount, d.currency)]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        <Pill status={d.status} />
      </div>
      <div className={styles.fields}>
        <Field label="Starts" value={formatDateAbsolute(d.startDate)} />
        <Field label="Ends" value={formatDateAbsolute(d.endDate)} />
        <Field
          label="Next charge"
          value={formatDateAbsolute(d.nextChargeDate)}
        />
        <Field label="Recurring" value={d.isRecurring ? 'Yes' : 'No'} />
        <Field label="Payment method" value={humanize(d.paymentMethod)} full />
        <Field label="Cancellation reason" value={d.cancellationReason} full />
      </div>
      <OpenButton
        href={d.userId ? `/dashboard/user/${d.userId}/subscription` : null}
        label="Open subscriptions ↗"
      />
    </div>
  );
};

const RiskProfileCard = ({selection}) => {
  const d = selection.data;
  const profile = d.profile || {};
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble fallback={<ProfileOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>Risk profile</span>
          <span className={styles.subtitle}>{humanize(profile.riskTier)}</span>
        </div>
      </div>
      <div className={styles.fields}>
        <Field label="Accreditation" value={humanize(profile.accreditation)} />
        <Field label="License" value={humanize(profile.license)} />
        {profile.scoring && typeof profile.scoring === 'object'
          ? Object.entries(profile.scoring)
              .filter(([, v]) => v !== null && v !== undefined && v !== '')
              .map(([k, v]) => (
                <Field
                  key={k}
                  label={humanize(k)}
                  value={typeof v === 'object' ? JSON.stringify(v) : String(v)}
                />
              ))
          : null}
      </div>
      <OpenButton
        href={d.userId ? `/dashboard/user/${d.userId}/risk-profile` : null}
        label="Open risk profile ↗"
      />
    </div>
  );
};

const SegmentCard = ({selection}) => {
  const d = selection.data;
  const segNames = Array.isArray(d.segments)
    ? d.segments
        .map(s => s?.name)
        .filter(Boolean)
        .join(', ')
    : '';
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble fallback={<TeamOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>
            Segment: {humanize(d.priority) || '—'}
          </span>
          <span className={styles.subtitle}>{segNames || '—'}</span>
        </div>
      </div>
      <div className={styles.fields}>
        <Field label="Daba Pro" value={humanize(d.dabaProStatus)} />
        <Field label="CS agent" value={d.customerSuccessAgent?.name} />
        <Field
          label="Last generated"
          value={formatDateAbsolute(d.lastGeneratedDate)}
          full
        />
      </div>
      <OpenButton
        href={d.userId ? `/dashboard/user/${d.userId}/overview` : null}
        label="Open user ↗"
      />
    </div>
  );
};

const WatchlistCard = ({selection}) => {
  const d = selection.data;
  const lists = Array.isArray(d.watchlists) ? d.watchlists : [];
  const headName = lists[0]?.name || '—';
  const more = lists.length > 1 ? ` … and ${lists.length - 1} more` : '';
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble fallback={<TagsOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>Watchlists ({lists.length})</span>
          <span className={styles.subtitle}>
            {headName}
            {more}
          </span>
        </div>
      </div>
      {lists.length ? (
        <div className={styles.fields}>
          {lists.slice(0, 3).map((wl, i) => (
            <Field
              key={i}
              label={wl?.name || `Watchlist ${i + 1}`}
              value={
                Array.isArray(wl?.companies) && wl.companies.length
                  ? wl.companies
                      .slice(0, 3)
                      .map(c => c?.ticker || c?.name)
                      .filter(Boolean)
                      .join(', ') +
                    (wl.companies.length > 3
                      ? ` (+${wl.companies.length - 3})`
                      : '')
                  : '—'
              }
              full
            />
          ))}
        </div>
      ) : null}
      <OpenButton
        href={d.userId ? `/dashboard/user/${d.userId}/overview` : null}
        label="Open user ↗"
      />
    </div>
  );
};

const StockCard = ({selection}) => {
  const d = selection.data;
  const company = d.company || {};
  const pct = typeof d.percentChange === 'number' ? d.percentChange : null;
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble
          imgUrl={company.logoImgURL}
          fallback={<LineChartOutlined />}
        />
        <div className={styles.titleBlock}>
          <span className={styles.title}>{d.symbol || company.ticker}</span>
          <span className={styles.subtitle}>{company.name || '—'}</span>
        </div>
        {d.tradeable === false ? <Pill status="DISABLED" /> : null}
      </div>
      <div className={styles.fields}>
        <Field
          label="Last price"
          value={formatMoney(d.lastPrice, d.baseCurrencyCode)}
        />
        <Field
          label="Change"
          value={pct === null ? '—' : `${pct > 0 ? '+' : ''}${pct.toFixed(2)}%`}
          tone={pct === null ? undefined : pct >= 0 ? 'positive' : 'negative'}
        />
        <Field label="Exchange" value={humanize(company.stockExchange)} full />
      </div>
      <OpenButton
        href={
          company.id
            ? `/dashboard/opportunities/startups/profile/${company.id}`
            : null
        }
        label="Open company ↗"
      />
    </div>
  );
};

const FundCard = ({selection}) => {
  const d = selection.data;
  const issuer = d.issuer?.entity || {};
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble imgUrl={issuer.logoImgURL} fallback={<FundOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>{d.name || '—'}</span>
          <span className={styles.subtitle}>
            {[d.ticker, d.currencyInfo?.currencyCode]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        <Pill status={d.status} />
      </div>
      <div className={styles.fields}>
        <Field
          label="YTD"
          value={d.YTDReturn != null ? `${d.YTDReturn}%` : null}
        />
        <Field
          label="Last year"
          value={d.lastYearReturn != null ? `${d.lastYearReturn}%` : null}
        />
        <Field
          label="Avg annual"
          value={
            d.averageAnnualReturn != null ? `${d.averageAnnualReturn}%` : null
          }
        />
        <Field
          label="Projected"
          value={
            d.projectedAnnualReturn != null
              ? `${d.projectedAnnualReturn}%`
              : null
          }
        />
        <Field label="Risk" value={humanize(d.investmentRisk)} />
        <Field
          label="Sharia"
          value={
            d.isShariaCompliant === true
              ? 'Yes'
              : d.isShariaCompliant === false
              ? 'No'
              : null
          }
        />
        <Field label="Issuer" value={issuer.name} full />
      </div>
      <OpenButton
        href={buildAssetMgmtHref('mutual-funds', 'FUNDS', d.id)}
        label="Open fund ↗"
      />
    </div>
  );
};

// /dashboard/asset-management/update/{kind}/{assetClass}/{id}/{lang}
// `kind` is the route segment ("mutual-funds" / "bond"); `assetClass` is
// the literal enum the route expects in the URL ("FUNDS" / "BONDS").
// Lang defaults to EN — there's no per-admin language preference wired up
// in this surface yet; the edit page lets you flip it once you're inside.
const buildAssetMgmtHref = (kind, assetClass, id, lang = 'EN') => {
  if (!id) return null;
  return `/dashboard/asset-management/update/${kind}/${assetClass}/${id}/${lang}`;
};

const BondCard = ({selection}) => {
  const d = selection.data;
  const issuer = d.issuer?.entity || {};
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble imgUrl={issuer.logoImgURL} fallback={<BankOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>{d.name || '—'}</span>
          <span className={styles.subtitle}>
            {[d.ticker, d.couponRate != null ? `${d.couponRate}%` : null]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        <Pill status={d.status} />
      </div>
      <div className={styles.fields}>
        <Field label="Risk" value={humanize(d.investmentRisk)} />
        <Field label="Liquidity" value={humanize(d.liquidity)} />
        <Field
          label="Sharia"
          value={
            d.isShariaCompliant === true
              ? 'Yes'
              : d.isShariaCompliant === false
              ? 'No'
              : null
          }
        />
        <Field label="Issuer" value={issuer.name} full />
      </div>
      <OpenButton
        href={buildAssetMgmtHref('bond', 'BONDS', d.id)}
        label="Open bond ↗"
      />
    </div>
  );
};

const CampaignCard = ({selection}) => {
  const d = selection.data;
  const fr = d.fundraiser || {};
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble imgUrl={fr.logoImgURL} fallback={<RocketOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>{fr.companyName || '—'}</span>
          <span className={styles.subtitle}>
            {[humanize(d.listingType), humanize(d.stage)]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        <Pill status={d.campaignStatus} />
      </div>
      <div className={styles.fields}>
        <Field label="Offering" value={humanize(d.offeringType)} />
        <Field
          label="Target"
          value={formatMoney(d.fundingTarget, d.currency)}
        />
        <Field label="Valuation" value={formatMoney(d.valuation, d.currency)} />
        <Field
          label="Min investment"
          value={formatMoney(d.minimumInvestment, d.currency)}
        />
        <Field
          label="Price/security"
          value={formatMoney(d.pricePerSecurity, d.currency)}
        />
        <Field label="Starts" value={formatDateAbsolute(d.startDate)} />
        <Field label="Ends" value={formatDateAbsolute(d.endDate)} />
      </div>
      <OpenButton
        href={
          fr.id ? `/dashboard/opportunities/startups/profile/${fr.id}` : null
        }
        label="Open company ↗"
      />
    </div>
  );
};

const TradeCard = ({selection}) => {
  const d = selection.data;
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble fallback={<RiseOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>{humanize(d.type)}</span>
          <span className={styles.subtitle}>
            {[shortenId(d.tradeId), formatDateTimeAbsolute(d.transactionDate)]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        <Pill status={d.transactionStatus} />
      </div>
      <div className={styles.fields}>
        <Field
          label="Amount"
          value={formatMoney(d.transactionAmount, d.assetCurrency)}
        />
        <Field
          label="Units"
          value={formatMoney(d.transactionUnits, null, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 6,
          })}
        />
        <Field
          label="Invested"
          value={formatMoney(d.investedAmount, d.assetCurrency)}
        />
        <Field label="Payment" value={humanize(d.paymentMethod)} />
        <Field
          label="Settlement"
          value={formatDateAbsolute(d.settlementDate)}
          full
        />
      </div>
      <OpenButton
        href={
          d.userId && d.tradeId
            ? `/dashboard/user/${d.userId}/update-transaction/${d.tradeId}`
            : null
        }
        label="Open trade ↗"
      />
    </div>
  );
};

const OrderCard = ({selection}) => {
  const d = selection.data;
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble fallback={<ShoppingCartOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>
            {[humanize(d.direction), d.symbol].filter(Boolean).join(' ')}
          </span>
          <span className={styles.subtitle}>
            {[
              humanize(d.type),
              d.quantity && d.price
                ? `${d.quantity} @ ${formatMoney(d.price, d.assetCurrency)}`
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        <Pill status={d.status} />
      </div>
      <div className={styles.fields}>
        <Field label="Asset class" value={humanize(d.assetClass)} />
        <Field label="Currency" value={d.assetCurrency} />
        <Field label="Created" value={formatDateSmart(d.createdAt)} full />
      </div>
      <OpenButton
        href={d.id ? `/dashboard/stocks/view-order/${d.id}` : null}
        label="Open order ↗"
      />
    </div>
  );
};

const DividendCard = ({selection}) => {
  const d = selection.data;
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble fallback={<GiftOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>
            Dividend: {formatMoney(d.payoutAmount, d.payoutCurrency)}
          </span>
          <span className={styles.subtitle}>
            {[shortenId(d.companyId), formatDateAbsolute(d.payoutDate)]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        <Pill status={d.status} />
      </div>
      <div className={styles.fields}>
        <Field
          label="Per share"
          value={formatMoney(d.amountPerShare, d.payoutCurrency)}
        />
        <Field label="Units" value={d.units} />
        <Field label="Tax" value={d.taxName} />
        <Field
          label="Tax %"
          value={d.taxPercentage != null ? `${d.taxPercentage}%` : null}
        />
        <Field
          label="Period"
          value={
            d.period?.startDate || d.period?.endDate
              ? `${formatDateAbsolute(
                  d.period?.startDate,
                )} – ${formatDateAbsolute(d.period?.endDate)}`
              : null
          }
          full
        />
      </div>
      <OpenButton href="/dashboard/dividends/payouts" label="Open payouts ↗" />
    </div>
  );
};

const RecurringCard = ({selection}) => {
  const d = selection.data;
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble fallback={<ReloadOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>
            Recurring: {humanize(d.frequency)}
          </span>
          <span className={styles.subtitle}>
            {[humanize(d.assetType), formatMoney(d.amount, d.currency)]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        <Pill status={d.status} />
      </div>
      <div className={styles.fields}>
        <Field label="Symbol" value={d.symbol} />
        <Field label="Next run" value={formatDateAbsolute(d.nextRunDate)} />
        <Field
          label="Last purchased"
          value={formatDateSmart(d.lastPurchasedDate)}
          full
        />
      </div>
      <OpenButton
        href={
          d.userId ? `/dashboard/user/${d.userId}/recurring-investment` : null
        }
        label="Open recurring ↗"
      />
    </div>
  );
};

const CollectionCard = ({selection}) => {
  const d = selection.data;
  const fr = d.financialReturns || {};
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble imgUrl={d.iconUrl} fallback={<DollarOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>{d.name || '—'}</span>
          <span className={styles.subtitle}>
            {[d.ticker, humanize(d.industry)].filter(Boolean).join(' · ')}
          </span>
        </div>
        <Pill status={d.status} />
      </div>
      <div className={styles.fields}>
        <Field label="Daba score" value={d.dabaScore} />
        <Field label="Companies" value={d.companies?.length} />
        <Field label="YTD" value={fr.YTD != null ? `${fr.YTD}%` : null} />
        <Field label="Today" value={fr.today != null ? `${fr.today}%` : null} />
      </div>
      <OpenButton
        href={d.id ? `/dashboard/collections/${d.id}` : null}
        label="Open collection ↗"
      />
    </div>
  );
};

const DocumentCard = ({selection}) => {
  const d = selection.data;
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble fallback={<FileTextOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>{d.documentName || 'Document'}</span>
          <span className={styles.subtitle}>
            {[humanize(d.documentCategory), formatDateAbsolute(d.issuedDate)]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        <Pill status={d.isActive ? 'ACTIVE' : 'ARCHIVED'} />
      </div>
      <OpenButton href={d.documentUrl} label="Download ↗" />
    </div>
  );
};

const NotificationCard = ({selection}) => {
  const d = selection.data;
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble imgUrl={d.iconUrl} fallback={<BellOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>{d.title || 'Notification'}</span>
          <span className={styles.subtitle}>
            {[humanize(d.source?.eventType), formatDateSmart(d.createdAt)]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
        <Pill status={d.isRead ? 'READ' : 'UNREAD'} />
      </div>
      <div className={styles.fields}>
        <Field label="Body" value={d.body} full />
        <Field label="Category" value={humanize(d.category)} />
        <Field label="Expires" value={formatDateAbsolute(d.expiresAt)} />
      </div>
      <OpenButton
        href={d.id ? `/dashboard/notification/update/${d.id}` : null}
        label="Open notification ↗"
      />
    </div>
  );
};

// Fallback when __typename doesn't match any known variant. Renders the raw
// keys so the agent still gets *something* useful, prefixed with a "kind"
// label so we know it's not a designed card.
const GenericCard = ({selection}) => {
  const d = selection.data;
  const entries = Object.entries(d || {}).filter(
    ([k, v]) =>
      k !== '__typename' &&
      v !== null &&
      v !== undefined &&
      v !== '' &&
      typeof v !== 'object',
  );
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <IconBubble fallback={<ProfileOutlined />} />
        <div className={styles.titleBlock}>
          <span className={styles.title}>
            {humanize(d.__typename || selection.bucket || 'Selection')}
          </span>
          {selection.label ? (
            <span className={styles.subtitle}>{selection.label}</span>
          ) : null}
        </div>
      </div>
      {entries.length ? (
        <div className={styles.fields}>
          {entries.slice(0, 8).map(([k, v]) => (
            <Field key={k} label={humanize(k)} value={String(v)} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

const VARIANTS = {
  TransactionEntry: TransactionCard,
  UserContext: UserContextCard,
  KYCStatusContext: KycCard,
  PortfolioPosition: PortfolioCard,
  SubscriptionRecord: SubscriptionCard,
  UserRiskProfileContext: RiskProfileCard,
  UserSegmentContext: SegmentCard,
  WatchlistPerformanceContext: WatchlistCard,
  StockEntry: StockCard,
  FundEntry: FundCard,
  BondEntry: BondCard,
  CampaignEntry: CampaignCard,
  TradeEntryFull: TradeCard,
  OrderEntry: OrderCard,
  DividendPayoutEntry: DividendCard,
  RecurringInvestmentEntry: RecurringCard,
  CollectionEntry: CollectionCard,
  UserDocumentEntry: DocumentCard,
  UserNotificationEntry: NotificationCard,
};

/* ------------------------------------------------------------------ */
/*  Exported renderer                                                 */
/* ------------------------------------------------------------------ */

export const SelectionCards = ({events}) => {
  const selections = useMemo(() => collectSelections(events), [events]);

  if (!selections.length) {
    return (
      <div className={styles.emptyState}>
        No specific item selected for this issue — the user typed a free-text
        question. See the User Info panel below for the customer’s context.
      </div>
    );
  }

  return (
    <div className={styles.stack}>
      {selections.map(sel => {
        const Variant = VARIANTS[sel.data.__typename] || GenericCard;
        return <Variant key={sel.bucket} selection={sel} />;
      })}
    </div>
  );
};
